import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw } from "react-icons/fi";
import { getSubCategories, createSubCategory, updateSubCategory, deleteSubCategory } from "../../services/subCategoryService";
import { getCategories } from "../../services/categoryService";

const SubCategoryManagement = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sortField, setSortField] = useState("displayOrder");
  const [sortValue, setSortValue] = useState("ASC");
  const [categoryId, setCategoryId] = useState("");
  const [isActive, setIsActive] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [formData, setFormData] = useState({
    categoryId: "",
    name: "",
    description: "",
    isActive: true,
    displayOrder: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();

  // Fetch categories for dropdown
  const { data: categoriesData } = useQuery(
    ["categories"],
    () => getCategories({ page: 1, limit: 1000 }),
    { refetchOnWindowFocus: false }
  );

  const categories = categoriesData?.data?.data || [];

  const { data: subCategoriesData, isLoading, refetch } = useQuery(
    ["subCategories", sortField, sortValue, categoryId, isActive],
    () => getSubCategories({
      page: 1,
      limit: 1000,
      sortField,
      sortValue,
      categoryId,
      isActive
    }),
    {
      onError: (error) => {
        toast.error(error.message || "Failed to load sub-categories");
      },
      refetchOnWindowFocus: false,
    }
  );

  const createSubCategoryMutation = useMutation(
    (data) => createSubCategory(data),
    {
      onSuccess: () => {
        toast.success("Sub-category created successfully");
        queryClient.invalidateQueries("subCategories");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to create sub-category");
      },
    }
  );

  const updateSubCategoryMutation = useMutation(
    (data) => updateSubCategory(data),
    {
      onSuccess: () => {
        toast.success("Sub-category updated successfully");
        queryClient.invalidateQueries("subCategories");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to update sub-category");
      },
    }
  );

  const deleteSubCategoryMutation = useMutation(
    (subCategoryId) => deleteSubCategory(subCategoryId),
    {
      onSuccess: () => {
        toast.success("Sub-category deleted successfully");
        queryClient.invalidateQueries("subCategories");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to delete sub-category");
      },
    }
  );

  const allSubCategories = React.useMemo(() => {
    if (!subCategoriesData || !subCategoriesData.data) return [];
    return Array.isArray(subCategoriesData.data.data) ? subCategoriesData.data.data : [];
  }, [subCategoriesData]);

  const filteredSubCategories = React.useMemo(() => {
    if (!searchKey.trim()) return allSubCategories;
    const searchTerm = searchKey.toLowerCase().trim();
    return allSubCategories.filter(subCat => 
      subCat.name?.toLowerCase().includes(searchTerm) ||
      subCat.slug?.toLowerCase().includes(searchTerm)
    );
  }, [allSubCategories, searchKey]);

  const subCategories = React.useMemo(() => {
    const startIndex = (page - 1) * limit;
    return filteredSubCategories.slice(startIndex, startIndex + limit);
  }, [filteredSubCategories, page, limit]);

  const totalCount = filteredSubCategories.length;
  const totalPages = Math.ceil(totalCount / limit);

  const resetForm = () => {
    setFormData({
      categoryId: "",
      name: "",
      description: "",
      isActive: true,
      displayOrder: 0
    });
    setSelectedSubCategory(null);
  };

  const handleCreate = () => {
    setModalType("create");
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (subCategory) => {
    setModalType("edit");
    setSelectedSubCategory(subCategory);
    setFormData({
      categoryId: subCategory.categoryId?.toString() || "",
      name: subCategory.name || "",
      description: subCategory.description || "",
      isActive: subCategory.isActive !== undefined ? subCategory.isActive : true,
      displayOrder: subCategory.displayOrder || 0
    });
    setShowModal(true);
  };

  const handleDelete = (subCategory) => {
    if (window.confirm(`Are you sure you want to delete sub-category "${subCategory.name}"?`)) {
      deleteSubCategoryMutation.mutate(subCategory.id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        categoryId: parseInt(formData.categoryId)
      };
      if (modalType === "create") {
        await createSubCategoryMutation.mutateAsync(submitData);
      } else {
        await updateSubCategoryMutation.mutateAsync({
          subCategoryId: selectedSubCategory.id,
          ...submitData
        });
      }
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    setPage(1);
  }, [searchKey]);

  const getCategoryName = (catId) => {
    const category = categories.find(c => c.id === catId);
    return category?.name || "Unknown";
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sub-Category Management</h1>
        <p className="text-gray-600">Manage product sub-categories</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search sub-categories..."
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
              />
            </div>

            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            <select
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => refetch()}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <FiRefreshCw size={16} className="mr-2" />
              Refresh
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
            >
              <FiPlus size={16} className="mr-2" />
              Add Sub-Category
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : subCategories.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">No sub-categories found</td>
                </tr>
              ) : (
                subCategories.map((subCat) => (
                  <tr key={subCat.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{subCat.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getCategoryName(subCat.categoryId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subCat.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {subCat.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={() => handleEdit(subCat)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded">
                          <FiEdit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(subCat)} className="text-red-600 hover:text-red-900 p-1 rounded">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalType === "create" ? "Create Sub-Category" : "Edit Sub-Category"}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === "true" })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                  >
                    {isSubmitting ? "Saving..." : modalType === "create" ? "Create" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubCategoryManagement;

